import React from 'react';
import { Button, Chip, styled, Typography } from '@mui/material';
import {
  AccountBalanceWalletOutlined,
  CalendarMonthOutlined,
  CreditCardOutlined,
  EventAvailableOutlined,
  LocalOfferOutlined,
  MedicalServicesOutlined,
  PersonOutline,
  ReceiptLongOutlined,
  StickyNote2Outlined,
} from '@mui/icons-material';
import Dialog from 'components/dialogs/Dialog';
import { Guide } from 'types/guide';

const palette = {
  ink: '#1f1723',
  muted: '#6d6470',
  border: '#eadde1',
  soft: '#fff8f5',
  primary: '#7b1235',
  primaryDark: '#4a0e1a',
  success: '#11853d',
  danger: '#b42318',
};

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
});

const Hero = styled('section')(({ theme }) => ({
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  padding: 16,
  background: 'linear-gradient(135deg, rgba(123,18,53,0.08) 0%, rgba(255,248,245,0.96) 58%, rgba(255,255,255,1) 100%)',
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 14,
  alignItems: 'center',
  boxShadow: '0 12px 30px rgba(74, 14, 26, 0.08)',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const HeroTitle = styled('div')({
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
  minWidth: 0,
});

const HeroIcon = styled('div')({
  width: 42,
  height: 42,
  borderRadius: 8,
  color: '#fff',
  background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
});

const HeroValue = styled('div')(({ theme }) => ({
  border: '1px solid rgba(123,18,53,0.18)',
  background: '#fff',
  borderRadius: 8,
  padding: '10px 14px',
  minWidth: 190,
  textAlign: 'right',
  [theme.breakpoints.down('sm')]: {
    minWidth: 'unset',
    textAlign: 'left',
  },
}));

const InfoGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 10,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Card = styled('section')({
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  background: '#fff',
  padding: 12,
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
});

const IconBox = styled('div')({
  width: 34,
  height: 34,
  borderRadius: 8,
  background: '#fff4f7',
  color: palette.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
});

const MainGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1.2fr .8fr',
  gap: 12,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Panel = styled('section')({
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  background: '#fff',
  overflow: 'hidden',
});

const PanelHeader = styled('div')({
  padding: '12px 14px',
  borderBottom: `1px solid ${palette.border}`,
  background: 'linear-gradient(135deg, #fff 0%, #fff8f5 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
});

const PanelBody = styled('div')({
  padding: '2px 14px',
});

const Row = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 12,
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: `1px solid ${palette.border}`,
  '&:last-child': {
    borderBottom: 0,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: 4,
  },
}));

const PaymentList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '12px 0',
});

const PaymentCard = styled('div')({
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  background: palette.soft,
  padding: 10,
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 10,
  alignItems: 'center',
});

const Actions = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 2,
});

interface ViewGuideProps {
  guide: Guide;
  onExited(): void;
}

const ViewGuide: React.FC<ViewGuideProps> = ({ guide, onExited }) => {
  const paymentMethods = guide.payment_methods || [];

  return (
    <Dialog maxWidth="md" title={`Guia nº ${guide.id}`} onExited={onExited}>
      <Container>
        <Hero>
          <HeroTitle>
            <HeroIcon>
              <ReceiptLongOutlined />
            </HeroIcon>
            <div>
              <Typography variant="h5" color={palette.ink}>
                Guia #{guide.id}
              </Typography>
              <Typography variant="body2" color={palette.muted}>
                {guide.formattedProcedureName || guide.procedure?.name || 'Consulta não informada'}
              </Typography>
              <div style={{ marginTop: 8 }}>
                <Chip
                  size="small"
                  color={guide.status === 'deleted'
                      ? 'error'
                      : guide.status === 'completed'
                        ? 'success'
                        : guide.status === 'no_show'
                          ? 'warning'
                          : 'primary'}
                  label={guide.formattedStatus || guide.status}
                />
              </div>
            </div>
          </HeroTitle>

          <HeroValue>
            <Typography variant="caption" color="textSecondary">
              Valor total
            </Typography>
            <Typography variant="h5" color={palette.primary} fontWeight={800}>
              {guide.formattedValueTotal}
            </Typography>
          </HeroValue>
        </Hero>

        <InfoGrid>
          <Card>
            <IconBox>
              <PersonOutline fontSize="small" />
            </IconBox>
            <div>
              <Typography variant="caption" color="textSecondary">
                Paciente
              </Typography>
              <Typography variant="body2" fontWeight={800}>
                {guide.formattedPatientName || guide.patient?.name || 'Não informado'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                CPF: {guide.patient?.document_number || 'Não informado'}
              </Typography>
            </div>
          </Card>

          <Card>
            <IconBox>
              <MedicalServicesOutlined fontSize="small" />
            </IconBox>
            <div>
              <Typography variant="caption" color="textSecondary">
                Médico
              </Typography>
              <Typography variant="body2" fontWeight={800}>
                {guide.formattedDoctorName || guide.doctor?.name || 'Não informado'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Marcador: {guide.formattedMarkerName || guide.marker?.name || 'Não informado'}
              </Typography>
            </div>
          </Card>

          <Card>
            <IconBox>
              <EventAvailableOutlined fontSize="small" />
            </IconBox>
            <div>
              <Typography variant="caption" color="textSecondary">
                Data da consulta
              </Typography>
              <Typography variant="body2" fontWeight={800}>
                {guide.formattedAppointmentDate || 'Não informado'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Criada em {guide.formattedCreatedAt || 'Não informado'}
              </Typography>
            </div>
          </Card>
        </InfoGrid>

        <MainGrid>
          <Panel>
            <PanelHeader>
              <div>
                <Typography variant="subtitle2" color={palette.ink}>
                  Detalhes da guia
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Consulta, criação e observações
                </Typography>
              </div>
              <Chip size="small" label={`Criado por ${guide.created_by || 'não informado'}`} />
            </PanelHeader>

            <PanelBody>
              <Row>
                <Card style={{ border: 0, padding: 0 }}>
                  <IconBox>
                    <LocalOfferOutlined fontSize="small" />
                  </IconBox>
                  <div>
                    <Typography variant="body2" fontWeight={800}>
                      Consulta
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {guide.formattedProcedureName || guide.procedure?.name || 'Não informado'}
                    </Typography>
                  </div>
                </Card>
                <Typography variant="body2" fontWeight={800} color={palette.primary}>
                  {guide.formattedValueProcedure}
                </Typography>
              </Row>

              <Row>
                <Card style={{ border: 0, padding: 0 }}>
                  <IconBox>
                    <CalendarMonthOutlined fontSize="small" />
                  </IconBox>
                  <div>
                    <Typography variant="body2" fontWeight={800}>
                      Criação
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {guide.formattedCreatedAt || 'Não informado'} • {guide.created_by || 'Não informado'}
                    </Typography>
                  </div>
                </Card>
              </Row>

              <Row>
                <Card style={{ border: 0, padding: 0 }}>
                  <IconBox>
                    <StickyNote2Outlined fontSize="small" />
                  </IconBox>
                  <div>
                    <Typography variant="body2" fontWeight={800}>
                      Observações
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {guide.observation || '-'}
                    </Typography>
                  </div>
                </Card>
              </Row>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <div>
                <Typography variant="subtitle2" color={palette.ink}>
                  Financeiro
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Valores e formas de pagamento
                </Typography>
              </div>
              <AccountBalanceWalletOutlined color="primary" fontSize="small" />
            </PanelHeader>

            <PanelBody>
              <Row>
                <Typography variant="body2" color="textSecondary">
                  Valor do marcador
                </Typography>
                <Typography variant="body2" fontWeight={800}>
                  {guide.formattedValueMarker}
                </Typography>
              </Row>
              <Row>
                <Typography variant="body2" color="textSecondary">
                  Valor da consulta
                </Typography>
                <Typography variant="body2" fontWeight={800}>
                  {guide.formattedValueProcedure}
                </Typography>
              </Row>
              <Row>
                <Typography variant="body2" color="textSecondary">
                  Desconto
                </Typography>
                <Typography variant="body2" fontWeight={800} color={palette.danger}>
                  {guide.formattedValueDiscount}
                </Typography>
              </Row>
              <Row>
                <Typography variant="body2" color="textSecondary">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={800} color={palette.success}>
                  {guide.formattedValueTotal}
                </Typography>
              </Row>

              <PaymentList>
                {paymentMethods.length > 0 ? (
                  paymentMethods.map(payment => (
                    <PaymentCard key={payment.id}>
                      <div>
                        <Typography variant="body2" fontWeight={800}>
                          {payment.payment_method?.name || 'Forma de pagamento'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {payment.installments || 1} parcela(s)
                        </Typography>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <CreditCardOutlined color="primary" fontSize="small" />
                        <Typography variant="caption" color="textSecondary" display="block">
                          {payment.formattedValueTotal || guide.formattedValueTotal}
                        </Typography>
                      </div>
                    </PaymentCard>
                  ))
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    Nenhuma forma de pagamento informada.
                  </Typography>
                )}
              </PaymentList>
            </PanelBody>
          </Panel>
        </MainGrid>

        <Actions>
          <Button variant="outlined" color="primary" onClick={onExited}>
            Fechar visualização
          </Button>
        </Actions>
      </Container>
    </Dialog>
  );
};

export default ViewGuide;
